import { BrowserModule } from '@angular/platform-browser';
import { NgModule, Injectable, Inject, InjectionToken, ErrorHandler } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { CrmCallbackComponent } from './components/crm-callback/crm-callback.component';
import { AppComponent } from './app.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import localeFr from '@angular/common/locales/fr';
import { TreeviewModule } from './lib/ngx-treeview';

import { WINDOW_PROVIDERS } from './shared/services/window.service';
import { CommonModule, registerLocaleData } from '@angular/common';
import { SafeHtmlPipe } from './shared/pipes/safeHtml.pipe';
import { SortTextPipe } from './shared/pipes/sortText.pipe';
import { InterleavePipe } from './shared/pipes/interleave.pipe';
import { ArticlesPageComponent } from './components/articles-page/articles-page.component';
import { TrackingInterceptor } from './shared/interceptors/tracking.interceptor';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { LoginComponent } from './components/login/login.component';
import { RegisterComponent } from './components/register/register.component';
import { VerifyEmailComponent } from './components/register/verify-email/verify-email.component';
import { CreatePasswordComponent } from './components/register/create-password/create-password.component';
import Rollbar from 'rollbar';
import { environment } from 'src/environments/environment';
import { HeaderComponent } from './components/header/header.component';
import { SearchPageComponent } from './components/search-page/search-page.component';
import { ContentTabsComponent } from './components/content-tabs/content-tabs.component';
import { FilterComponent } from './components/filter/filter.component';
import { StatsPageComponent } from './components/stats-page/stats-page.component';
import { AdminDocCardComponent } from './components/admin-doc-card/admin-doc-card.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { InsertTextPipe } from './shared/pipes/insert-text.pipe';
import { ArticleCardComponent } from './components/article-card/article-card.component';
import { ContentComponent } from './components/content/content.component';
import { ArticleViewComponent } from './components/documents-view/article-view/article-view.component';
import { AdminDocsPageComponent } from './components/admin-docs-page/admin-docs-page.component';
import { AdminDocViewComponent } from './components/documents-view/admin-doc-view/admin-doc-view.component';
import { ResultMessageComponent } from './components/result-message/result-message.component';
import { TerritorySearchPageComponent } from './components/territory-search-page/territory-search-page.component';
import { ImpactersPageComponent } from './components/impacters-page/impacters-page.component';
import { ImpacterCardComponent } from './components/impacter-card/impacter-card.component';
import { ImpacterViewComponent } from './components/impacter-view/impacter-view.component';
import { TerritoryWatchesPageComponent } from './components/territory-watches-page/territory-watches-page.component';
import { WizardComponent } from './components/wizard/wizard.component';
import { StepsComponent } from './components/steps/steps.component';
import { StepHeaderComponent } from './components/step-header/step-header.component';
import { TopicStepTemplateComponent } from './components/topic-step-template/topic-step-template.component';
import { SourceStepTemplateComponent } from './components/source-step-template/source-step-template.component';
import { FrequentStepTemplateComponent } from './components/frequent-step-template/frequent-step-template.component';
import { OverviewStepTemplateComponent } from './components/overview-step-template/overview-step-template.component';
import { PerimeterStepTemplateComponent } from './components/perimeter-step-template/perimeter-step-template.component';
import { SortDatePipe } from './shared/pipes/sortDate.pipe';
import { TopicBlockComponent } from './components/topic-block/topic-block.component';
import { TopicTermComponent } from './components/topic-term/topic-term.component';
import { TopicsPageComponent } from './components/topics-page/topics-page.component';
import { ListTemplateComponent } from './components/list-template/list-template.component';
import { TopicFormComponent } from './components/topic-form/topic-form.component';
import { ModalConfirmationComponent } from './components/modal-confirmation/modal-confirmation.component';
import { SimpleTypeGuideTemplateComponent } from './components/simple-type-guide-template/simple-type-guide-template.component';
import { NearTypeGuideTemplateComponent } from './components/near-type-guide-template/near-type-guide-template.component';
import { CombinationTypeGuideTemplateComponent } from './components/combination-type-guide-template/combination-type-guide-template.component';
import { ExampleGuideTemplateComponent } from './components/example-guide-template/example-guide-template.component';
import { TopicBlockSelectComponent } from './components/topic-block-select/topic-block-select.component';
import { ExcludeTypeGuideTemplateComponent } from './components/exclude-type-guide-template/exclude-type-guide-template.component';
import { ExplicativeBlockOneComponent } from './components/explicative-block-one/explicative-block-one.component';
import { ExplicativeBlockTwoComponent } from './components/explicative-block-two/explicative-block-two.component';
import { OutlineNoneDirective } from './shared/directives/appearanceDirective/outline-none.directive';
import { ForgotPasswordComponent } from './components/password/forgot-password.component';
import { ForgotPasswordExpiredComponent } from './components/password/forgot-password-expired/forgot-password-expired.component';
import { ForgotPasswordIncorrectComponent } from './components/password/forgot-password-incorrect/forgot-password-incorrect.component';
import { ResetPasswordComponent } from './components/password/reset-password/reset-password.component';
import { ResetPasswordSuccessComponent } from './components/password/reset-password-success/reset-password-success.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AdminTopicComponent } from './components/admin-topic/admin-topic.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoaderdialogComponent } from './components/loaderdialog/loaderdialog.component';
import { TopicListTemplateComponent } from './components/topic-list-template/topic-list-template.component';
import { PlaceholderNoTopicComponent } from './components/placeholder-no-topic/placeholder-no-topic.component';
import { ModalExplanationComponent } from './components/modal-explanation/modal-explanation.component';
import { ToastMessageStackComponent } from './components/toast-message-stack/toast-message-stack.component';
import { ReversePipe } from './shared/pipes/reverse.pipe';
import { MaterialModule } from './material/material.module';
import { IconModule } from './shared/icon/icon.module';
import { ShowIfTruncatedDirective } from './shared/directives/appearanceDirective/show-if-truncated.directive';
import { ArticleGuestViewComponent } from './components/documents-view/article-guest-view/article-guest-view.component';
import { AdminDocGuestViewComponent } from './components/documents-view/admin-doc-guest-view/admin-doc-guest-view.component';
import { GuestContentComponent } from './components/guest-content/guest-content.component';
import { TerritoriesPanelComponent } from './components/documents-view/territories-panel/territories-panel.component';
import { WelcomePanelComponent } from './components/documents-view/welcome-panel/welcome-panel.component';
import { AdminDocPdfComponent } from './components/documents-view/admin-doc-pdf/admin-doc-pdf.component';
import { FoldersPageComponent } from './components/folders-page/folders-page.component';
import { ItemListComponent } from './components/item-list/item-list.component';
import { FolderViewComponent } from './components/folder-view/folder-view.component';
import { ItemListPaginationComponent } from './components/item-list-pagination/item-list-pagination.component';
import { FolderContentComponent } from './components/folder-content/folder-content.component';
import { SavingPanelComponent } from './components/documents-view/saving-panel/saving-panel.component';
import Utils from './shared/utils';
import { ModalCreationComponent } from './components/modal-creation/modal-creation.component';
import { FolderViewContainerComponent } from './components/folder-view-container/folder-view-container.component';
import { ModalEditFolderComponent } from './components/modal-edit-folder/modal-edit-folder.component';
import { ModalDeleteFolderComponent } from './components/modal-delete-folder/modal-delete-folder.component';
import { ModalRenameItemComponent } from './components/modal-rename-item/modal-rename-item.component';
import { BookmarkCreatedAtPipe } from './shared/pipes/bookmark-created-at.pipe';
import { MetadataComponent } from './components/documents-view/metadata/metadata.component';
import { ImageCarouselComponent } from './components/documents-view/image-carousel-smart/image-carousel/image-carousel.component';
import { ImageCarouselSmartComponent } from './components/documents-view/image-carousel-smart/image-carousel-smart.component';
import { ImageFallbackDirective } from './shared/directives/appearanceDirective/image-fallback.directive';
import { ModalNPSFormComponent } from './components/modal-nps-form/modal-nps-form.component';
import { SafeResourceUrlPipe } from './shared/pipes/safe-resource-url.pipe';
import { ExplainFaqComponent } from './components/explain-faq/explain-faq.component';
import { ExportPanelComponent } from './components/documents-view/export-panel/export-panel.component';
import { AdminDocSearchPaginationComponent } from './components/admin-doc-search-pagination/admin-doc-search-pagination.component';
import { KeywordsCustomToolbarComponent } from './components/keywords-custom-toolbar/keywords-custom-toolbar.component';
import { AdminDocSidebarButtonComponent } from './components/admin-doc-sidebar-button/admin-doc-sidebar-button.component';
import { RouterModule } from '@angular/router';
import { ClickOrEnterConfirmationDirective } from './shared/directives/behaviourDirective/click-or-enter-confirmation.directive';
import { OpenRenameModalDirective } from './shared/directives/behaviourDirective/open-rename-modal.directive';
import { ReferencePipe } from './shared/pipes/reference.pipe';
import { TrackingDirective } from './shared/directives/behaviourDirective/tracking.directive';
import { PrefAndMraeDocCardComponent } from './components/pref-and-mrae-doc-card/pref-and-mrae-doc-card.component';
import { IconTextBadgeComponent } from './core/icon-text-badge/icon-text-badge.component';
import { AssociatedPanelComponent } from './components/associated-panel/associated-panel.component';
import { MiddleClickDirective } from './shared/directives/middle-click.directive';
import { TendersModule } from './tenders/tenders.module';
import { CommonExplainModule, HttpLoaderFactory } from './common-explain/common-explain.module';
import { SharePanelComponent } from './components/documents-view/share-panel/share-panel.component';
import { RegionAndDepartmentDocCardComponent } from './components/region-and-department-doc-card/region-and-department-doc-card.component';
import { TenderStatusListComponent } from './tenders/components/tender-status-list/tender-status-list.component';
import { TaskReminderComponent } from './components/task-reminder/task-reminder.component';
import {
  ReferencesCustomActionsDirective,
  ReferencesEmptyDirective,
  ReferencesErrorDirective,
  ReferencesLoadingDirective,
  ReferencesPanelComponent,
  ReferencesPlaceholderDirective,
} from './components/references-panel/references-panel.component';
import { ImpersonateComponent } from './components/ImpersonateComponent/impersonate.component';
import { AddinConnectComponent } from './components/addin-connect/addin-connect.component';
import { SmartSearchPanelComponent } from './components/smart-search-panel/smart-search-panel.component';
import {
  ContentEmptyDirective,
  ContentFooterDirective,
  ContentListDirective,
  ContentLoadingDirective,
  ContentPanelComponent,
  ContentPlaceholderDirective,
} from './components/content-panel/content-panel.component';
import { SmartSearchComponent } from './components/smart-search/smart-search.component';
import { MarkdownModule } from 'ngx-markdown';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { ExPeriodSelectorComponent } from './common-explain/components/ex-period-selector/ex-period-selector.component';
import { SmartSearchPromptModule } from './shared/modules/smart-search-prompt/smart-search-prompt.module';
import { NgxTurnstileModule } from 'ngx-turnstile';

const rollbarConfig = {
  accessToken: 'b6b684e6d7b84fc09b4ee2ec42740834',
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: environment.ROLLBAR_ENV,
  },
};

export const RollbarService = new InjectionToken<Rollbar>('rollbar');

@Injectable()
export class RollbarErrorHandler implements ErrorHandler {
  constructor(@Inject(RollbarService) private rollbar: Rollbar) {}

  handleError(err: object): void {
    if (!environment.production) {
      console.error(err);
    }
    this.rollbar.error(err); // ['originalError'] || err);
  }
}

export function rollbarFactory() {
  return new Rollbar(rollbarConfig);
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    VerifyEmailComponent,
    CreatePasswordComponent,
    SafeHtmlPipe,
    SortTextPipe,
    SortDatePipe,
    InterleavePipe,
    ReferencePipe,
    ArticlesPageComponent,
    HeaderComponent,
    SearchPageComponent,
    ContentTabsComponent,
    FilterComponent,
    StatsPageComponent,
    AdminDocCardComponent,
    PaginationComponent,
    InsertTextPipe,
    ArticleCardComponent,
    ContentComponent,
    ArticleViewComponent,
    AdminDocsPageComponent,
    AdminDocViewComponent,
    ResultMessageComponent,
    TerritorySearchPageComponent,
    ImpactersPageComponent,
    ImpacterCardComponent,
    ImpacterViewComponent,
    TerritoryWatchesPageComponent,
    WizardComponent,
    StepsComponent,
    StepHeaderComponent,
    TopicStepTemplateComponent,
    SourceStepTemplateComponent,
    FrequentStepTemplateComponent,
    OverviewStepTemplateComponent,
    PerimeterStepTemplateComponent,
    TopicBlockComponent,
    TopicTermComponent,
    TopicsPageComponent,
    ListTemplateComponent,
    TopicFormComponent,
    ModalConfirmationComponent,
    SimpleTypeGuideTemplateComponent,
    NearTypeGuideTemplateComponent,
    CombinationTypeGuideTemplateComponent,
    ExampleGuideTemplateComponent,
    TopicBlockSelectComponent,
    ExcludeTypeGuideTemplateComponent,
    ExplicativeBlockOneComponent,
    ExplicativeBlockTwoComponent,
    OutlineNoneDirective,
    ForgotPasswordComponent,
    ForgotPasswordExpiredComponent,
    ForgotPasswordIncorrectComponent,
    ResetPasswordComponent,
    ResetPasswordSuccessComponent,
    SettingsComponent,
    AdminTopicComponent,
    LoaderdialogComponent,
    TopicListTemplateComponent,
    PlaceholderNoTopicComponent,
    ProgressBarComponent,
    LoaderdialogComponent,
    ModalExplanationComponent,
    ToastMessageStackComponent,
    ReversePipe,
    ShowIfTruncatedDirective,
    ArticleGuestViewComponent,
    AdminDocGuestViewComponent,
    GuestContentComponent,
    TerritoriesPanelComponent,
    WelcomePanelComponent,
    AdminDocPdfComponent,
    FoldersPageComponent,
    ItemListComponent,
    FolderViewComponent,
    ItemListPaginationComponent,
    FolderContentComponent,
    SavingPanelComponent,
    ModalCreationComponent,
    FolderViewContainerComponent,
    ModalEditFolderComponent,
    ModalDeleteFolderComponent,
    ModalRenameItemComponent,
    BookmarkCreatedAtPipe,
    MetadataComponent,
    ImageCarouselComponent,
    ImageCarouselSmartComponent,
    ImageFallbackDirective,
    ModalNPSFormComponent,
    SafeResourceUrlPipe,
    ExplainFaqComponent,
    ExportPanelComponent,
    AdminDocSearchPaginationComponent,
    KeywordsCustomToolbarComponent,
    AdminDocSidebarButtonComponent,
    ClickOrEnterConfirmationDirective,
    OpenRenameModalDirective,
    ExportPanelComponent,
    TrackingDirective,
    PrefAndMraeDocCardComponent,
    IconTextBadgeComponent,
    AssociatedPanelComponent,
    MiddleClickDirective,
    SharePanelComponent,
    RegionAndDepartmentDocCardComponent,
    TaskReminderComponent,
    ImpersonateComponent,
    CrmCallbackComponent,
    AddinConnectComponent,
  ],
  imports: [
    CommonModule,
    RouterModule,
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    TreeviewModule.forRoot(),
    NgxExtendedPdfViewerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    BrowserAnimationsModule,
    MaterialModule,
    IconModule,
    RouterModule,
    TendersModule,
    CommonExplainModule,
    TenderStatusListComponent,
    ReferencesPanelComponent,
    ReferencesPlaceholderDirective,
    ReferencesLoadingDirective,
    ReferencesEmptyDirective,
    ReferencesErrorDirective,
    ReferencesCustomActionsDirective,
    SmartSearchPanelComponent,
    ContentPanelComponent,
    ContentPlaceholderDirective,
    ContentListDirective,
    ContentFooterDirective,
    ContentLoadingDirective,
    ContentEmptyDirective,
    SmartSearchPanelComponent,
    SmartSearchComponent,
    MarkdownModule.forRoot(),
    SearchBarComponent,
    SmartSearchPromptModule,
    ExPeriodSelectorComponent,
    NgxTurnstileModule,
  ],
  providers: [
    provideHttpClient(withInterceptors([authInterceptor]), withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: TrackingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
    WINDOW_PROVIDERS,
    Utils,
  ],
  bootstrap: [AppComponent],
  exports: [ProgressBarComponent],
})
export class AppModule {}

registerLocaleData(localeFr, 'fr');
